import React, { useState, useRef } from 'react';
import { QrReader } from 'react-qr-reader';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function PaymentTerminalSimulator() {
  const [step, setStep] = useState('idle'); // idle, amount, scanning, processing, success, error
  const [amount, setAmount] = useState('');
  const [merchantName] = useState('Commerce Breton');
  const [terminalId] = useState('TPE-BKB-00' + Math.floor(Math.random() * 1000));
  const [scannedCode, setScannedCode] = useState('');
  const [transactionResult, setTransactionResult] = useState(null);
  const [error, setError] = useState('');
  const [useManualInput, setUseManualInput] = useState(false);
  const [manualCode, setManualCode] = useState('');

  const handleAmountSubmit = (e) => {
    e.preventDefault();
    if (parseFloat(amount) > 0) {
      setStep('scanning');
      setError('');
    }
  };

  const handleScan = async (result) => {
    if (result && step === 'scanning') {
      setScannedCode(result.text);
      await processPayment(result.text);
    }
  };

  const handleManualCodeSubmit = async () => {
    if (manualCode.trim()) {
      setScannedCode(manualCode);
      await processPayment(manualCode);
    }
  };

  const processPayment = async (code) => {
    setStep('processing');
    setError('');

    try {
      const response = await axios.post(`${API_URL}/tpe/payment/execute`, null, {
        params: {
          transaction_code: code,
          amount: parseFloat(amount),
          merchant_name: merchantName,
          merchant_id: 'MER-BKB-001',
          terminal_id: terminalId
        }
      });

      setTransactionResult(response.data);
      setStep('success');
      
      // Revenir à l'écran initial après 5 secondes
      setTimeout(() => {
        resetTerminal();
      }, 5000);

    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setStep('error');
      
      // Revenir au scan après 3 secondes
      setTimeout(() => {
        setStep('scanning');
        setError('');
      }, 3000);
    }
  };

  const resetTerminal = () => {
    setStep('idle');
    setAmount('');
    setScannedCode('');
    setTransactionResult(null);
    setError('');
    setManualCode('');
    setUseManualInput(false);
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* TPE Device Frame */}
        <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-3xl p-8 shadow-2xl border-4 border-gray-700">
          
          {/* Header */}
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <img src="/logo-bkb.jpg" alt="BKB" className="w-8 h-8 rounded" />
              <h1 className="text-white text-xl font-bold">Bank Kreiz Breizh</h1>
            </div>
            <p className="text-gray-400 text-sm">Terminal de Paiement</p>
            <p className="text-gray-500 text-xs">Terminal ID: {terminalId}</p>
          </div>

          {/* Screen */}
          <div className="bg-blue-50 rounded-2xl p-6 mb-6 min-h-[400px] flex flex-col justify-center">
            
            {/* IDLE State */}
            {step === 'idle' && (
              <form onSubmit={handleAmountSubmit} className="space-y-4">
                <div className="text-center mb-6">
                  <div className="text-6xl mb-4">💳</div>
                  <h2 className="text-2xl font-bold text-gray-900">Nouveau Paiement</h2>
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Montant à payer
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full px-4 py-4 text-3xl text-center border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                      autoFocus
                      required
                    />
                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-2xl text-gray-500">€</span>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full py-4 bg-green-600 text-white rounded-xl text-xl font-bold hover:bg-green-700 transition-colors"
                >
                  Continuer ➜
                </button>
              </form>
            )}

            {/* AMOUNT Display */}
            {step === 'amount' && (
              <div className="text-center">
                <p className="text-gray-600 text-sm mb-2">Montant</p>
                <p className="text-5xl font-bold text-gray-900 mb-6">{parseFloat(amount).toFixed(2)} €</p>
                <button
                  onClick={() => setStep('scanning')}
                  className="w-full py-4 bg-blue-600 text-white rounded-xl text-xl font-bold"
                >
                  Scanner QR Code
                </button>
              </div>
            )}

            {/* SCANNING State */}
            {step === 'scanning' && (
              <div className="space-y-4">
                <div className="text-center mb-4">
                  <p className="text-2xl font-bold text-gray-900 mb-2">
                    {parseFloat(amount).toFixed(2)} €
                  </p>
                  <p className="text-gray-600">Scannez le QR code client</p>
                </div>

                {!useManualInput ? (
                  <>
                    <div className="bg-white rounded-xl overflow-hidden border-4 border-blue-500">
                      <QrReader
                        onResult={handleScan}
                        constraints={{ facingMode: 'environment' }}
                        className="w-full"
                      />
                    </div>
                    <p className="text-center text-sm text-gray-600">
                      📱 Présentez le QR code BKB Pay
                    </p>
                  </>
                ) : (
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={manualCode}
                      onChange={(e) => setManualCode(e.target.value)}
                      placeholder="BKB-xxxxxxxx-timestamp-code"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-sm font-mono"
                    />
                    <button
                      onClick={handleManualCodeSubmit}
                      className="w-full py-3 bg-blue-600 text-white rounded-lg font-bold"
                    >
                      Valider le code
                    </button>
                  </div>
                )}

                <button
                  onClick={() => setUseManualInput(!useManualInput)}
                  className="w-full py-2 bg-gray-200 text-gray-700 rounded-lg text-sm"
                >
                  {useManualInput ? '📷 Utiliser la caméra' : '⌨️ Saisir le code manuellement'}
                </button>

                <button
                  onClick={resetTerminal}
                  className="w-full py-2 bg-red-100 text-red-700 rounded-lg"
                >
                  Annuler
                </button>
              </div>
            )}

            {/* PROCESSING State */}
            {step === 'processing' && (
              <div className="text-center">
                <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-blue-600 mx-auto mb-6"></div>
                <p className="text-2xl font-bold text-gray-900 mb-2">Traitement en cours...</p>
                <p className="text-gray-600">Veuillez patienter</p>
              </div>
            )}

            {/* SUCCESS State */}
            {step === 'success' && transactionResult && (
              <div className="text-center space-y-4">
                <div className="text-6xl mb-4">✅</div>
                <h2 className="text-3xl font-bold text-green-600">PAIEMENT ACCEPTÉ</h2>
                
                <div className="bg-white rounded-xl p-4 text-left space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Montant:</span>
                    <span className="font-bold text-xl">{transactionResult.amount.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Carte:</span>
                    <span className="font-semibold">{transactionResult.card_type} •••• {transactionResult.card_last4}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Titulaire:</span>
                    <span className="font-semibold">{transactionResult.cardholder_name}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-600">N° Reçu:</span>
                    <span className="font-mono text-sm">{transactionResult.receipt_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transaction:</span>
                    <span className="font-mono text-xs">{transactionResult.transaction_id.substring(0, 16)}...</span>
                  </div>
                </div>

                <p className="text-sm text-gray-600">
                  {new Date(transactionResult.timestamp).toLocaleString('fr-FR')}
                </p>
              </div>
            )}

            {/* ERROR State */}
            {step === 'error' && (
              <div className="text-center">
                <div className="text-6xl mb-4">❌</div>
                <h2 className="text-2xl font-bold text-red-600 mb-4">PAIEMENT REFUSÉ</h2>
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-4">
                  <p className="text-red-800 font-semibold">{error}</p>
                </div>
                <p className="text-sm text-gray-600">Retour au scan...</p>
              </div>
            )}
          </div>

          {/* Keypad Simulation */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <button className="bg-gray-700 text-white py-3 rounded-lg font-bold hover:bg-gray-600">1</button>
            <button className="bg-gray-700 text-white py-3 rounded-lg font-bold hover:bg-gray-600">2</button>
            <button className="bg-gray-700 text-white py-3 rounded-lg font-bold hover:bg-gray-600">3</button>
          </div>

          {/* Status LED */}
          <div className="flex items-center justify-center gap-2">
            <div className={`w-3 h-3 rounded-full ${step === 'success' ? 'bg-green-500' : step === 'error' ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
            <span className="text-gray-400 text-xs">
              {step === 'idle' ? 'Prêt' : step === 'success' ? 'Transaction OK' : step === 'error' ? 'Erreur' : 'En cours'}
            </span>
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 text-center">
          <p className="text-gray-400 text-sm">🔒 Simulateur de Terminal de Paiement</p>
          <p className="text-gray-500 text-xs mt-1">Bank Kreiz Breizh - Pour test uniquement</p>
        </div>
      </div>
    </div>
  );
}

export default PaymentTerminalSimulator;
